import CollapsibleCard from '@/components/shared/CollapsibleCard'
import IDVerifiedIcon from '@/components/ui/icons/IDVerifiedIcon'
import PotentialDuplicateChip from '@/components/ui/PotentialDuplicateChip'
import { formatDate } from '@/lib/utils'
import type { APIUserPatient } from '@/services/users'
import { colors, sxPresets } from '@/theme'
import { ArrowForward } from '@mui/icons-material'
import { Box, Paper, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'

interface UserPatientsSectionProps {
  patients: APIUserPatient[]
  sectionId?: string
  title?: string
}

export default function UserPatientsSection({
  patients,
  sectionId = 'user-patients',
  title = 'Patients'
}: UserPatientsSectionProps) {
  const navigate = useNavigate()
  const isEmpty = !patients || patients.length === 0

  return (
    <CollapsibleCard sectionId={sectionId} title={title} isEmpty={isEmpty} emptyMessage='No patients found'>
      <Box
        sx={{
          ...sxPresets().section.content,
          bgcolor: colors.background.surface,
          ...sxPresets().grid.threeColumn,
          ...sxPresets(200).scrollable.twoRows
        }}
      >
        {patients?.map(patient => (
          <Paper
            key={patient.hash}
            onClick={() =>
              navigate(`/patients/${patient.hash}`, {
                state: {
                  backLabel: 'Back to User',
                  fallbackPath: '/users'
                }
              })
            }
            elevation={0}
            sx={{
              ...sxPresets().card.default,
              ...sxPresets().card.clickable,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}
          >
            {/* Card Header */}
            <Box sx={sxPresets().section.header} />

            {/* Card Body */}
            <Box sx={{ px: 2, pb: 2, flex: 1, bgcolor: '#fff' }}>
              {patient.isPotentialDuplicate && (
                <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                  <PotentialDuplicateChip />
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant='cardTitle'>
                  {patient.firstName} {patient.lastName}
                </Typography>
                {patient.idVerified && <IDVerifiedIcon />}
              </Box>
              <Typography variant='smallText' sx={{ mb: 0.5 }}>
                Date of birth:{' '}
                <Box component='span' sx={{ fontWeight: 600, color: colors.text.primary }}>
                  {formatDate(patient.dob)}
                </Box>
              </Typography>
              <Typography variant='smallText'>
                Gender:{' '}
                <Box component='span' sx={{ fontWeight: 600, color: colors.text.primary }}>
                  {patient.gender}
                </Box>
              </Typography>
            </Box>

            {/* Card Footer */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                py: 1.5,
                bgcolor: colors.brand.primaryLight
              }}
            >
              <Typography variant='link' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                View patient
                <ArrowForward sx={{ fontSize: '14px' }} />
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>
    </CollapsibleCard>
  )
}
